import ChildCareIcon from '@mui/icons-material/ChildCare';
import DevicesIcon from '@mui/icons-material/Devices';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Snackbar,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import JukoBoxLogo from '../components/JukoBoxLogo';
import PageTransition from '../components/PageTransition';

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [isLoading, setIsLoading] = React.useState(false);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleSpotifyCallback = async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get('token');
      const spotify = params.get('spotify');
      const id = params.get('id');

      if (token && id) {
        setIsLoading(true);
        setOpenSnackbar(true);
        // Stocker les informations d'authentification
        localStorage.setItem('access_token', token);
        if (spotify) {
          localStorage.setItem('spotify_access', spotify);
        }

        // Rediriger vers le dashboard
        navigate('/dashboard');
      }
    };

    handleSpotifyCallback();
  }, [location, navigate]);

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="h6" color="primary">
          Connexion en cours...
        </Typography>
      </Box>
    );
  }

  return (
    <PageTransition>
      <Box sx={{ bgcolor: 'background.default' }}>
        <Snackbar
          open={openSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ mt: 2 }}
        >
          <Alert
            severity="info"
            icon={<CircularProgress size={20} color="inherit" />}
            sx={{
              width: '100%',
              bgcolor: 'rgba(255, 107, 107, 0.1)',
              color: 'primary.main',
              '& .MuiAlert-icon': {
                color: 'primary.main',
              },
            }}
          >
            Connexion à Spotify en cours...
          </Alert>
        </Snackbar>

        {/* Hero Section */}
        <Box
          sx={{
            position: 'relative',
            pt: { xs: 8, md: 12 },
            pb: { xs: 8, md: 12 },
            overflow: 'hidden',
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 4,
                alignItems: 'center',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Box sx={{ mb: 4 }}>
                  <JukoBoxLogo size={90} />
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    mb: 3,
                    fontWeight: 700,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    lineHeight: 1.2,
                    color: 'text.primary',
                  }}
                >
                  La musique à portée de main pour vos enfants
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 4,
                    color: 'text.secondary',
                    fontWeight: 400,
                    fontSize: { xs: '1rem', md: '1.25rem' },
                  }}
                >
                  Transformez la chambre de votre enfant avec Jukobox et votre compte Spotify : un
                  simple geste libère chansons et histoires infinies, un bonheur instantané.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/login')}
                    startIcon={<PlayArrowIcon />}
                    sx={{
                      borderRadius: '50px',
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      boxShadow: '0 4px 14px 0 rgba(255, 107, 107, 0.2)',
                      '&:hover': {
                        boxShadow: '0 6px 20px 0 rgba(255, 107, 107, 0.3)',
                      },
                    }}
                  >
                    Commencer maintenant
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/store')}
                    sx={{
                      borderRadius: '50px',
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      '&:hover': {
                        borderColor: theme.palette.primary.dark,
                        backgroundColor: 'rgba(255, 107, 107, 0.05)',
                      },
                    }}
                  >
                    Découvrir les produits
                  </Button>
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    position: 'relative',
                    height: { xs: 400, md: 500 },
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/img/box-illustration.png"
                    alt="Illustration de la Jukobox"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Features Section */}
        <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.paper' }}>
          <Container maxWidth="lg">
            <Typography
              variant="h3"
              align="center"
              sx={{
                mb: 6,
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              Fonctionnalités principales
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
              <Card
                sx={{
                  flex: 1,
                  borderRadius: 4,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                <CardHeader
                  avatar={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255, 107, 107, 0.1)',
                      }}
                    >
                      <ChildCareIcon sx={{ color: 'primary.main', fontSize: 40 }} />
                    </Box>
                  }
                  title={
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      Un geste, et la magie sonore démarre
                    </Typography>
                  }
                  sx={{ pb: 0 }}
                />
                <CardContent sx={{ pt: 2 }}>
                  <Typography variant="body1" color="text.secondary">
                    Pose la carte sur la Jukobox : la chambre s'illumine d'une chanson entraînante,
                    d'un conte captivant ou d'un album complet. Pas d'attente, pas de bouton
                    compliqué — juste l'émerveillement immédiat qui fait briller les yeux des
                    enfants.
                  </Typography>
                </CardContent>
              </Card>

              <Card
                sx={{
                  flex: 1,
                  borderRadius: 4,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                <CardHeader
                  avatar={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: 'rgba(78, 205, 196, 0.1)',
                      }}
                    >
                      <DevicesIcon sx={{ color: 'secondary.main', fontSize: 40 }} />
                    </Box>
                  }
                  title={
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      Autonomie pour lui, contrôle pour vous
                    </Typography>
                  }
                  sx={{ pb: 0 }}
                />
                <CardContent sx={{ pt: 2 }}>
                  <Typography variant="body1" color="text.secondary">
                    Votre enfant explore librement son univers musical ; vous, vous gardez la main
                    en coulisses. Depuis votre téléphone, ajustez le volume, choisissez les moments
                    de silence ou ajoutez de nouveaux contenus en deux clics. Il grandit en
                    confiance, vous restez serein.
                  </Typography>
                </CardContent>
              </Card>

              <Card
                sx={{
                  flex: 1,
                  borderRadius: 4,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                <CardHeader
                  avatar={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255, 107, 107, 0.1)',
                      }}
                    >
                      <MusicNoteIcon sx={{ color: 'primary.main', fontSize: 40 }} />
                    </Box>
                  }
                  title={
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      Un trésor infini de sons
                    </Typography>
                  }
                  sx={{ pb: 0 }}
                />
                <CardContent sx={{ pt: 2 }}>
                  <Typography variant="body1" color="text.secondary">
                    Berceuses pour s'endormir, hits pour danser, histoires pour voyager : des
                    milliers de pistes prêtes à accompagner chaque instant de la journée. La Jukobox
                    évolue avec ses goûts, transformant chaque carte en porte ouverte vers de
                    nouvelles aventures sonores.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Container>
        </Box>

        {/* Story Telling Section */}
        <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.default' }}>
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 6,
                alignItems: 'center',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    position: 'relative',
                    height: { xs: 300, md: 400 },
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/img/story-telling.png"
                    alt="Histoire de Jukobox"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 4,
                    fontWeight: 700,
                    color: 'text.primary',
                  }}
                >
                  Notre histoire
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    color: 'text.secondary',
                  }}
                >
                  Jukobox est né d'un besoin concret : donner à ma fille Louise, 4 ans, l'autonomie
                  de choisir ses chansons et histoires sans solliciter un adulte.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    color: 'text.secondary',
                  }}
                >
                  J'ai conçu un boîtier simple : une carte effleurée, et le contenu démarre
                  instantanément. Devant l'enthousiasme de Louise et l'intérêt de nos proches, j'ai
                  affiné le design, sécurisé la gestion parentale et ouvert la possibilité de
                  personnaliser les contenus.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    color: 'text.secondary',
                  }}
                >
                  Aujourd'hui, Jukobox transforme chaque chambre d'enfant en espace d'écoute
                  intuitif : un geste pour eux, un contrôle discret pour les parents, et une
                  expérience sonore toujours adaptée.
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            py: { xs: 8, md: 12 },
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: 'white',
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 4,
                alignItems: 'center',
              }}
            >
              <Box sx={{ flex: 2 }}>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    color: 'white',
                  }}
                >
                  Prêt à transformer l'expérience musicale de vos enfants ?
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 4,
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: 400,
                  }}
                >
                  Rejoignez des milliers de parents qui ont déjà adopté Jukobox pour offrir à leurs
                  enfants une expérience musicale interactive et amusante.
                </Typography>
              </Box>
              <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'right' } }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/register')}
                  sx={{
                    borderRadius: '50px',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    bgcolor: 'white',
                    color: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                    },
                  }}
                >
                  S'inscrire maintenant
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
    </PageTransition>
  );
};

export default Home;
